import React, { useEffect, useState, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useRequestData, useLabels, useFlags } from 'wsm-common-data';
import { formatNumber } from 'ddc-intl';
import { useSelector } from 'react-redux';
import Slider from 'rc-slider';
import Histogram from './Histogram';

import {
	rangeContainsInventory,
	getLowerBound,
	getUpperBound,
	fixRangeForSubmit,
	getBucketMap,
	isNoPriceActive,
	roundToNearestBucket
} from '../../utilities/facet-user-range';
import RangeInput from './RangeInput';
import useFetchFacets from '../../hooks/useFetchFacets';
import NoPriceButton from './NoPriceButton';
import { noPrice } from '../../global-constants';
import { getTotalCount } from '../../utilities/selector';
import { MODAL } from '../../constant';
import { rangeFormTracking } from '../../utilities/trackingFactory';

const rangeReducer = (state, action) => {
	switch (action.type) {
		case 'CHANGE_SLIDER_MIN':
			return {
				...state,
				// rc-slider adds to the min value to calculate max in a range slider
				sliderMin: action.payload.min === 1 ? 0 : action.payload.min,
				formInputMin: action.payload.min
			};
		case 'CHANGE_SLIDER_MAX':
			return {
				...state,
				sliderMax: action.payload.sliderMax,
				formInputMax: action.payload.max
			};
		case 'CHANGE_FORM_INPUT_MIN':
			return {
				...state,
				...(typeof action.payload.sliderMin === 'number' && {
					sliderMin:
						action.payload.sliderMin === 1
							? 0
							: action.payload.sliderMin
				}),
				formInputMin: action.payload.min
			};
		case 'CHANGE_FORM_INPUT_MAX':
			return {
				...state,
				...(typeof action.payload.sliderMax === 'number' && {
					sliderMax: action.payload.sliderMax
				}),
				formInputMax: action.payload.max
			};
		default:
			return {
				...state
			};
	}
};

const RangeForm = (props) => {
	const { facetValues, noPriceObj, facetId, isMultiselect, selectedValues } =
		props;
	const {
		min,
		max,
		bucketSize,
		values,
		selectedMin = null,
		selectedMax = null,
		currency: isCurrency = false
	} = facetValues;
	const { deviceType, locale, windowId, widgetName } = useRequestData();
	const vehicleCount = useSelector(getTotalCount);
	const labels = useLabels();
	const flags = useFlags();
	const flagHistograms = flags['ws-facet-browse-histograms'];

	const [filters, setFilter] = useState(null);
	useFetchFacets(filters, MODAL);

	const isDesktop = deviceType === 'DESKTOP';

	// Setting a variable to offset the range slider when bucketSize equals 1.
	// This `offset` variable is incorporated within the max/min state values
	// to allow the slider and histogram to display properly for "year" facets.
	const offset = bucketSize === 1 ? 1 : 0;

	// Lowest and highest buckets
	const bottomLimit = getLowerBound(min, bucketSize, facetId, isMultiselect);
	const topLimit = getUpperBound(max + offset, bucketSize);

	// Values of min and max forms, slider and histogram
	const currentMin =
		selectedMin !== null ? Math.max(selectedMin, bottomLimit) : bottomLimit;
	const currentMax =
		selectedMax !== null
			? Math.min(selectedMax, topLimit)
			: topLimit - offset;

	const [{ sliderMin, formInputMin, sliderMax, formInputMax }, dispatch] =
		useReducer(rangeReducer, {
			sliderMin: currentMin,
			formInputMin: currentMin,
			sliderMax: currentMax + offset,
			formInputMax: currentMax
		});

	// this useEffect is needed to update user ranges when filters are cleared / changed
	useEffect(() => {
		if (currentMin !== formInputMin) {
			dispatch({
				type: 'CHANGE_FORM_INPUT_MIN',
				payload: {
					min: currentMin,
					sliderMin: getLowerBound(
						currentMin,
						bucketSize,
						facetId,
						isMultiselect
					)
				}
			});
		}
		if (currentMax !== formInputMax) {
			dispatch({
				type: 'CHANGE_FORM_INPUT_MAX',
				payload: {
					max: currentMax + offset,
					sliderMax: getUpperBound(currentMax + offset, bucketSize)
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentMin, currentMax]);

	// Map of buckets containing values with bucket values as keys
	const bucketMap = getBucketMap(values);

	const handleSubmit = useCallback(
		(newMin, newMax, srcEvent) => {
			const isMinChange = newMin !== currentMin;
			const isNewRange = newMin !== currentMin || newMax !== currentMax;
			const [bottom, top] = fixRangeForSubmit(
				newMin,
				newMax,
				bottomLimit,
				topLimit,
				bucketSize,
				isMinChange
			);
			const containsInventory = rangeContainsInventory(
				bottom,
				top,
				bucketSize,
				bucketMap,
				facetId,
				isMultiselect
			);

			if (isNewRange && containsInventory) {
				if (isMinChange) {
					dispatch({
						type: 'CHANGE_FORM_INPUT_MIN',
						payload: {
							min: bottom,
							sliderMin: getLowerBound(
								bottom,
								bucketSize,
								facetId,
								isMultiselect
							)
						}
					});
				} else {
					dispatch({
						type: 'CHANGE_FORM_INPUT_MAX',
						payload: {
							max: top,
							sliderMax: getUpperBound(top + offset, bucketSize)
						}
					});
				}

				const updatedSelections = [];
				const sliderValue = `${bottom}-${top}`;

				updatedSelections.push(sliderValue);

				if (
					selectedValues &&
					selectedValues.find((value) => value.value === noPrice)
				) {
					updatedSelections.push(noPrice);
				}

				setFilter({ [facetId]: updatedSelections });
				rangeFormTracking(widgetName, windowId, facetId, srcEvent);
			}
			// case where new range isn't new  or doesn't contain inventory: change state back to current
			else {
				dispatch({
					type: 'CHANGE_FORM_INPUT_MIN',
					payload: { min: currentMin }
				});
				dispatch({
					type: 'CHANGE_FORM_INPUT_MAX',
					payload: { max: currentMax }
				});
			}
		},
		[
			currentMin,
			currentMax,
			bottomLimit,
			topLimit,
			bucketSize,
			bucketMap,
			facetId,
			isMultiselect,
			selectedValues,
			widgetName,
			windowId,
			offset
		]
	);

	const handleSliderSubmit = (newMin, newMax) => {
		handleSubmit(newMin, newMax - offset);
	};

	const updateSliderValues = (minVal, maxVal) => {
		const isMinChange = minVal !== formInputMin;
		if (isMinChange) {
			const bottomBucket = getLowerBound(
				minVal,
				bucketSize,
				facetId,
				isMultiselect
			);

			dispatch({
				type: 'CHANGE_SLIDER_MIN',
				payload: { min: bottomBucket }
			});
		} else {
			const topBucket = getUpperBound(maxVal, bucketSize);

			dispatch({
				type: 'CHANGE_SLIDER_MAX',
				payload: { sliderMax: topBucket, max: topBucket - offset }
			});
		}
	};

	const getVehicleCount = () => {
		const matchesLabel =
			vehicleCount > 1 ? labels.get('MATCHES') : labels.get('MATCH');
		return `${formatNumber(vehicleCount, locale)} ${matchesLabel}`;
	};

	return (
		<div className={`col-xs-12 ${!isDesktop && 'p-0'}`}>
			<RangeInput
				id={facetId}
				minValue={bottomLimit}
				maxValue={topLimit}
				minInput={formInputMin}
				maxInput={formInputMax}
				minLabel={labels.get('MIN')}
				maxLabel={labels.get('MAX')}
				handleSubmit={handleSubmit}
				isCurrency={isCurrency}
			/>
			<div className="text-center ddc-font-sizes-small text-neutral-400 mb-5">
				{getVehicleCount()}
			</div>
			<div className="facet-list-range-slider w-100 pb-4">
				{flagHistograms ? (
					<Histogram
						values={values}
						facetId={facetId}
						bottomLimit={bottomLimit}
						topLimit={topLimit}
						currentMin={sliderMin}
						currentMax={sliderMax}
						bucketMap={bucketMap}
						bucketSize={bucketSize}
					/>
				) : (
					<></>
				)}
				<Slider
					range
					allowCross={false} // don't allow sliders to cross
					pushable={bucketSize} // minimum ensured distance between handles
					defaultValue={[sliderMin, sliderMax]}
					value={sliderMin !== null ? [sliderMin, sliderMax] : null} // rc-slider focuses the handles if these are set initially
					min={bottomLimit}
					max={topLimit}
					dots
					step={bucketSize}
					onChange={(e) => {
						updateSliderValues(
							getLowerBound(
								e[0],
								bucketSize,
								facetId,
								isMultiselect
							),
							roundToNearestBucket(e[1], bucketSize)
						);
					}}
					onAfterChange={(e) => {
						handleSliderSubmit(
							getLowerBound(
								e[0],
								bucketSize,
								facetId,
								isMultiselect
							),
							roundToNearestBucket(e[1], bucketSize)
						);
					}}
				/>
				{noPriceObj && !!noPriceObj.count && (
					<NoPriceButton
						id={facetId}
						noPriceCount={noPriceObj.count}
						noPriceLabel={
							noPriceObj.label ||
							labels.get('INCLUDE_NO_PRICE_AVAILABLE')
						}
						selectedValues={selectedValues}
						isSelected={isNoPriceActive(selectedValues)}
						// Disable button when have no selectedValues
						isDisabled={selectedValues.length === 0}
					/>
				)}
			</div>
		</div>
	);
};

RangeForm.propTypes = {
	facetValues: PropTypes.shape({
		min: PropTypes.number.isRequired,
		max: PropTypes.number.isRequired,
		bucketSize: PropTypes.number.isRequired,
		values: PropTypes.arrayOf(
			PropTypes.shape({
				count: PropTypes.number.isRequired,
				label: PropTypes.string.isRequired,
				type: PropTypes.string.isRequired,
				value: PropTypes.string.isRequired
			})
		).isRequired,
		selectedMin: PropTypes.number,
		selectedMax: PropTypes.number,
		currency: PropTypes.bool
	}).isRequired,
	noPriceObj: PropTypes.shape({
		count: PropTypes.number,
		label: PropTypes.string
	}),
	facetId: PropTypes.string.isRequired,
	isMultiselect: PropTypes.bool.isRequired,
	selectedValues: PropTypes.arrayOf(PropTypes.shape({})).isRequired
};

RangeForm.defaultProps = {
	noPriceObj: null
};

export default RangeForm;
